import { mixSeed, hash3i, toUnit } from '@/core/math/hash';
import { billow3, fbm3, ridged3, simplex3, worley3 } from '@/core/math/noise';
import { clamp, clamp01, smoothstep } from '@/core/math/scalar';
import { TerrainProfile } from '@/universe/BodyDef';
import { BIOME_INDEX } from './BiomeDef';

/**
 * Deterministic planetary terrain generation.
 *
 * This module is the single source of truth for what a planet's surface looks
 * like. It is a *pure function of position and seed* with no state whatsoever,
 * for two reasons that are both load-bearing:
 *
 *  1. It runs in Web Workers. A worker shares no memory with the main thread, so
 *     purity is the only thing that guarantees the collision surface the physics
 *     uses matches the mesh the player sees.
 *
 *  2. Adjacent chunks must produce bit-identical vertices along their shared
 *     edge. Because every sample is taken at a *global* planet-space direction
 *     rather than a chunk-local coordinate, two chunks evaluating the same edge
 *     vertex necessarily get the same answer. No stitching, no seam fixing, no
 *     tolerance comparisons.
 *
 * The generator combines several techniques rather than layering octaves of one
 * noise function, because plain fractal noise reads as "bumpy" rather than as
 * geography:
 *
 *   - Tectonic plates from spherical Worley cells, with mountain belts raised
 *     along convergent boundaries and rift valleys cut along divergent ones.
 *   - A domain-warped continental mask, thresholded to hit a target ocean
 *     coverage.
 *   - Ridged multifractal mountains, masked by the tectonic belts so ranges run
 *     in coherent lines instead of appearing at random.
 *   - An erosion approximation that damps high-frequency amplitude on steep
 *     ground, producing smooth valley floors and sharp crests.
 *   - Crater fields with realistic rim/bowl/ejecta profiles for airless bodies.
 *   - Transverse dune fields aligned to a prevailing wind direction.
 *   - Volcanic fissure networks from inverted ridged noise.
 */

export interface TerrainParams {
  /** Planet id, folded into the seed. */
  planetId: string;
  /** Master seed for this body. */
  seed: number;
  /** Reference radius, metres. */
  radius: number;
  profile: TerrainProfile;
  /** Mean equatorial surface temperature, K. */
  meanTemperature: number;
  /** Equator-to-pole temperature drop, K. */
  polarDelta: number;
  /** Temperature lapse rate, K/m. */
  lapseRate: number;
  /** Biome ids permitted on this body. */
  biomes: string[];
  /** Absolute sea level relative to the datum, metres. -Infinity when dry. */
  seaLevel: number;
  /**
   * Value of the continental field at which land meets sea.
   *
   * Calibrated at construction so that exactly `oceanCoverage` of the surface
   * falls below it. Without this the authored coverage is only a hint, and a
   * planet asking for 63% ocean can easily generate 92%.
   */
  continentThreshold: number;
  /** Half-width of the coastal blend in continental-field units. */
  continentWidth: number;
  /**
   * Plate feature points on the unit sphere, `[x0,y0,z0, x1,y1,z1, ...]`,
   * `profile.plateCount` of them. Built once here (see `buildPlatePoints`)
   * rather than per sample, since `samplePlates` runs once per vertex of
   * every chunk.
   */
  platePoints: Float64Array;
}

export interface SurfaceSample {
  /** Elevation above the datum, metres. */
  elevation: number;
  /** Local temperature, K. */
  temperature: number;
  /** Local humidity, 0..1. */
  humidity: number;
  /** Terrain slope, radians. */
  slope: number;
  /** Chosen biome id. */
  biomeId: string;
  /** Numeric biome index for vertex packing. */
  biomeIndex: number;
  /** True when this point is below sea level on a body that has oceans. */
  underwater: boolean;
}

/** Builds terrain parameters from a body definition. */
export function terrainParamsFor(
  planetId: string,
  systemSeed: number,
  radius: number,
  profile: TerrainProfile,
  thermal: { meanSurfaceTemperature: number; polarDelta: number; lapseRate: number },
  biomes: string[],
): TerrainParams {
  const seed = mixSeed(systemSeed, hashPlanetId(planetId));
  const seaLevel =
    profile.seaLevelFraction <= -1 ? Number.NEGATIVE_INFINITY : profile.seaLevelFraction * profile.maxElevation;

  const params: TerrainParams = {
    planetId,
    seed,
    radius,
    profile,
    meanTemperature: thermal.meanSurfaceTemperature,
    polarDelta: thermal.polarDelta,
    lapseRate: thermal.lapseRate,
    biomes,
    seaLevel,
    continentThreshold: 0,
    continentWidth: 0.12,
    platePoints: buildPlatePoints(profile.plateCount, seed),
  };

  const calibration = calibrateContinents(params);
  params.continentThreshold = calibration.threshold;
  params.continentWidth = calibration.width;
  return params;
}

/**
 * Deterministic, near-equal-area feature points for `samplePlates`.
 *
 * The previous implementation ran Worley noise directly on a **cubic**
 * lattice (`Math.floor` of a scaled direction vector): `worley3`'s 3x3x3
 * neighbourhood is an axis-aligned integer grid, and the Voronoi cells of
 * that grid intersected with a sphere read as a visible regular tiling —
 * reported as a hexagon-like grid baked into distant terrain, worse on
 * bodies whose `plateCount` happened to produce coarse cells relative to the
 * view. See docs/NEXT_SESSION.md §3.
 *
 * This instead places points on a Fibonacci sphere (near-uniform area
 * coverage by construction, unlike a cubic lattice's unequal axis-aligned
 * cells), rotated by a per-seed random axis/angle so different planets don't
 * share one lattice orientation, then perturbed by a small per-point jitter
 * and renormalised so cell boundaries are irregular rather than a perfectly
 * even lattice. `samplePlates` brute-forces the nearest two of these — cheap
 * since `plateCount` is a handful to a few dozen, the same order as the 27
 * cells the old cubic search already visited per sample.
 */
function buildPlatePoints(plateCount: number, seed: number): Float64Array {
  const points = new Float64Array(Math.max(0, plateCount) * 3);
  if (plateCount <= 0) return points;

  const axisHash = hash3i(0, 0, 0, seed ^ 0x2f6e2b1);
  let ax = toUnit(axisHash);
  let ay = toUnit(axisHash ^ 0x9e3779b9);
  let az = toUnit(axisHash ^ 0x85ebca6b);
  const axisLen = Math.hypot(ax, ay, az) || 1;
  ax /= axisLen;
  ay /= axisLen;
  az /= axisLen;
  const angle = toUnit(hash3i(1, 0, 0, seed ^ 0x2f6e2b1)) * Math.PI * 2;
  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);

  const golden = Math.PI * (3 - Math.sqrt(5));
  // Small relative to the ~1/sqrt(plateCount) angular spacing between points,
  // so cells stay irregular without points crossing each other.
  const jitterScale = 0.35 / Math.sqrt(plateCount);

  for (let i = 0; i < plateCount; i++) {
    const y = 1 - (2 * (i + 0.5)) / plateCount;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = i * golden;
    const px = r * Math.cos(theta);
    const py = y;
    const pz = r * Math.sin(theta);

    // Rodrigues' rotation formula around the seeded (ax,ay,az) axis.
    const dot = px * ax + py * ay + pz * az;
    const crossX = ay * pz - az * py;
    const crossY = az * px - ax * pz;
    const crossZ = ax * py - ay * px;
    let rx = px * cosA + crossX * sinA + ax * dot * (1 - cosA);
    let ry = py * cosA + crossY * sinA + ay * dot * (1 - cosA);
    let rz = pz * cosA + crossZ * sinA + az * dot * (1 - cosA);

    const jitterHash = hash3i(i, 0, 0, seed ^ 0x68e31da4);
    rx += toUnit(jitterHash) * jitterScale;
    ry += toUnit(jitterHash ^ 0x9e3779b9) * jitterScale;
    rz += toUnit(jitterHash ^ 0x85ebca6b) * jitterScale;
    const len = Math.hypot(rx, ry, rz) || 1;
    points[i * 3] = rx / len;
    points[i * 3 + 1] = ry / len;
    points[i * 3 + 2] = rz / len;
  }
  return points;
}

/** Number of samples used to calibrate the continental threshold. */
const CALIBRATION_SAMPLES = 512;

/**
 * Chooses the continental threshold so the generated ocean coverage matches the
 * authored figure.
 *
 * The continental field's distribution depends on the plate count, the warp
 * strength and the noise seed, so there is no closed form for it. Instead the
 * field is sampled at a fixed Fibonacci-sphere set of directions and the
 * empirical quantile is taken. The sample set is fixed, so this is
 * deterministic: the same planet always calibrates to the same threshold, on
 * the main thread and in every worker.
 */
export function calibrateContinents(params: TerrainParams): { threshold: number; width: number } {
  // A dry world has no waterline to calibrate against, but it still needs a
  // reference level to separate basins from highlands, so the median is used.
  // Returning an infinite threshold here would collapse the continental mask to
  // a constant and take every mountain range on every airless body with it.
  const coverage = params.profile.oceanCoverage > 0 ? Math.min(0.995, params.profile.oceanCoverage) : 0.5;

  const values = new Float64Array(CALIBRATION_SAMPLES);
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < CALIBRATION_SAMPLES; i++) {
    // Fibonacci sphere: near-uniform area coverage, fully deterministic.
    const y = 1 - (2 * (i + 0.5)) / CALIBRATION_SAMPLES;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = i * golden;
    values[i] = continentField(r * Math.cos(theta), y, r * Math.sin(theta), params);
  }
  values.sort();

  const index = Math.min(CALIBRATION_SAMPLES - 1, Math.max(0, Math.round(coverage * (CALIBRATION_SAMPLES - 1))));
  const threshold = values[index];

  // Coastline blend width: a fraction of the field's spread, so shorelines are
  // a similar physical width on every planet regardless of its noise scale.
  const spread = values[Math.floor(CALIBRATION_SAMPLES * 0.84)] - values[Math.floor(CALIBRATION_SAMPLES * 0.16)];
  return { threshold, width: Math.max(0.02, spread * 0.18) };
}

function hashPlanetId(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

// Sub-seeds. Deriving each field from a distinct offset keeps them independent.
const SEED_WARP = 1301;
const SEED_CONTINENT = 2477;
const SEED_PLATES = 3691;
const SEED_MOUNTAIN = 4813;
const SEED_DETAIL = 5927;
const SEED_CRATER = 7043;
const SEED_DUNE = 8161;
const SEED_RIVER = 0x5217a3;
const SEED_FISSURE = 9277;
const SEED_HUMIDITY = 10391;
const SEED_EROSION = 11503;

/**
 * How many octaves are worth evaluating at a given sample spacing.
 *
 * Octaves whose wavelength is shorter than two samples cannot be represented
 * and only add aliasing noise, so they are skipped. This is both a correctness
 * measure and the main performance lever: an orbital-LOD chunk evaluates three
 * octaves where a ground-LOD chunk evaluates eight.
 */
/**
 * How many octaves this sampling rate can actually carry.
 *
 * Nyquist: an octave whose wavelength is shorter than twice the vertex spacing
 * cannot be represented, and sampling it anyway does not produce fine detail —
 * it produces noise at the vertex frequency, which on a displaced mesh is a
 * field of one-vertex spikes.
 *
 * Rounding *up* here admitted exactly one octave more than the mesh could
 * carry, at every level. That is where the spikes came from: the error is
 * largest at middle levels, where a chunk is still hundreds of metres between
 * vertices but the detail band has content at half that.
 */
function octavesFor(baseWavelength: number, sampleSpacing: number, maxOctaves: number): number {
  if (sampleSpacing <= 0) return maxOctaves;
  const limit = Math.log2(Math.max(1, baseWavelength / (2 * sampleSpacing))) + 1;
  return Math.max(1, Math.min(maxOctaves, Math.floor(limit)));
}

/** Converts a wavelength in metres into a noise-space frequency for unit directions. */
export function frequencyFor(radius: number, wavelengthMetres: number): number {
  return radius / Math.max(1, wavelengthMetres);
}

interface PlateInfo {
  /** Distance to the nearest plate boundary, 0 at the boundary, 1 far inside. */
  interior: number;
  /** Boundary character: >0 convergent (mountains), <0 divergent (rifts). */
  convergence: number;
  /** Oceanic character, 0 continental to 1 oceanic, blended at boundaries. */
  oceanicity: number;
  /** Stable per-plate value in 0..1. */
  variation: number;
}

/**
 * Spherical tectonic plates.
 *
 * Worley cells on the unit sphere give plate interiors; `f2 - f1` gives distance
 * to the boundary between the two nearest cells. Whether that boundary is
 * convergent or divergent is derived from the two cells' pseudo-random drift
 * directions, so a given boundary has one consistent character along its length
 * instead of flickering per sample.
 */
function samplePlates(dx: number, dy: number, dz: number, params: TerrainParams): PlateInfo {
  const p = params.profile;
  if (p.plateCount <= 0) {
    return { interior: 1, convergence: 0, oceanicity: 0, variation: 0.5 };
  }
  // Same normalisation the old cubic lattice used (spacing ~1 in this scaled
  // space), kept so the boundary/blend constants below don't need retuning.
  const scale = Math.sqrt(p.plateCount / (4 * Math.PI));
  const points = params.platePoints;
  let f1 = Infinity;
  let f2 = Infinity;
  let nearest = 0;
  for (let i = 0; i < p.plateCount; i++) {
    const ddx = points[i * 3] - dx;
    const ddy = points[i * 3 + 1] - dy;
    const ddz = points[i * 3 + 2] - dz;
    const d = Math.sqrt(ddx * ddx + ddy * ddy + ddz * ddz) * scale;
    if (d < f1) {
      f2 = f1;
      f1 = d;
      nearest = i;
    } else if (d < f2) {
      f2 = d;
    }
  }

  const boundaryDistance = f2 - f1;
  const boundary = clamp01(boundaryDistance * 2.2);
  const cellHash = hash3i(nearest, 0x51ed, 0, params.seed + SEED_PLATES);
  // Per-plate properties used to switch discontinuously at the exact Worley
  // bisector. The continental field therefore jumped by 0.48 in one sample,
  // which became a kilometre-high vertical wall after displacement. Fade every
  // plate toward a shared neutral shelf across a finite boundary zone. Both
  // sides therefore meet at exactly the same value, including triple junctions.
  const boundaryBlend = smoothstep(0, 0.045, boundaryDistance);
  const currentOceanicity = toUnit(cellHash) < 0.55 ? 1 : 0;
  const oceanicity = 0.5 + (currentOceanicity - 0.5) * boundaryBlend;
  const currentVariation = toUnit(cellHash ^ 0x5bf03635);
  const variation = 0.5 + (currentVariation - 0.5) * boundaryBlend;

  // Boundary character is a continuous global field. Basing it on the nearest
  // cell hash made it jump at the same bisector that it was supposed to shape.
  const convergence = simplex3(
    dx * scale * 1.7,
    dy * scale * 1.7,
    dz * scale * 1.7,
    params.seed + SEED_PLATES + 1,
  );

  return { interior: boundary, convergence, oceanicity, variation };
}

/**
 * Erosion approximation.
 *
 * A standard "damped fbm": each octave's contribution is attenuated by the
 * accumulated gradient of the octaves below it, so amplitude collapses where the
 * surface is already steep. The visible result is flat-floored valleys with
 * sharp dividing ridges, which is what fluvial erosion actually produces, at a
 * fraction of the cost of simulating it.
 */
function erodedFbm(
  x: number,
  y: number,
  z: number,
  frequency: number,
  octaves: number,
  seed: number,
  erosionStrength: number,
): number {
  let sum = 0;
  let norm = 0;
  let amp = 1;
  let freq = frequency;
  let gx = 0;
  let gy = 0;
  let gz = 0;
  const eps = 0.5 / Math.max(1, frequency);

  for (let o = 0; o < octaves; o++) {
    const s = seed + o * 977;
    const n = simplex3(x * freq, y * freq, z * freq, s);

    if (erosionStrength > 0) {
      const inv = 1 / (2 * eps * freq);
      gx += (simplex3((x + eps) * freq, y * freq, z * freq, s) - simplex3((x - eps) * freq, y * freq, z * freq, s)) * inv * amp;
      gy += (simplex3(x * freq, (y + eps) * freq, z * freq, s) - simplex3(x * freq, (y - eps) * freq, z * freq, s)) * inv * amp;
      gz += (simplex3(x * freq, y * freq, (z + eps) * freq, s) - simplex3(x * freq, y * freq, (z - eps) * freq, s)) * inv * amp;
    }

    const damping = erosionStrength > 0 ? 1 / (1 + erosionStrength * (gx * gx + gy * gy + gz * gz)) : 1;
    sum += n * amp * damping;
    norm += amp;
    amp *= 0.5;
    freq *= 2.02;
  }

  return norm > 0 ? sum / norm : 0;
}

/**
 * Crater field.
 *
 * Three size classes, each a Worley lattice whose cells either host a crater or
 * do not. The profile is a parabolic bowl plus a raised rim plus a low ejecta
 * blanket, which is what makes an impact surface read as impacted rather than
 * merely dented.
 */
function craterHeight(dx: number, dy: number, dz: number, params: TerrainParams): number {
  const p = params.profile;
  if (p.craterDensity <= 0) return 0;

  let total = 0;
  for (let cls = 0; cls < 3; cls++) {
    const radiusMetres = p.craterMaxRadius / Math.pow(3.4, cls);
    if (radiusMetres < 60) break;
    const scale = frequencyFor(params.radius, radiusMetres * 2.6);
    const seed = params.seed + SEED_CRATER + cls * 313;
    const w = worley3(dx * scale, dy * scale, dz * scale, seed, 1.0);

    // Only some cells host a crater; larger classes are rarer.
    const presence = toUnit(w.cell ^ 0x9e3779b9);
    const threshold = clamp01(p.craterDensity) * (1 - cls * 0.18);
    if (presence > threshold) continue;

    const sizeJitter = 0.45 + toUnit(w.cell ^ 0x85ebca6b) * 0.75;
    const craterRadius = 0.42 * sizeJitter;
    const d = w.f1 / craterRadius;
    if (d > 1.62) continue;

    // Depth scales with diameter, but large basins are proportionally much
    // shallower than small bowls, and nothing may exceed the body's relief
    // budget: a 90 km basin on a moon with 3 km of relief is a shallow dish,
    // not a 15 km hole.
    const depth = Math.min(radiusMetres * 0.14, p.maxElevation * 0.5) * sizeJitter;
    // Bowl, rim and ejecta blanket. The proportions roughly conserve mass:
    // material excavated from the bowl is what builds the rim and the blanket,
    // so a cratered surface stays centred on its datum instead of inflating.
    let h: number;
    if (d < 1.0) {
      // Bowl: parabolic floor.
      h = -depth * (1 - d * d);
    } else if (d < 1.18) {
      // Rim: raised lip.
      const u = (d - 1.0) / 0.18;
      h = depth * 0.3 * Math.sin(u * Math.PI);
    } else {
      // Ejecta: a thin blanket decaying with the inverse cube of distance.
      const u = (d - 1.18) / 0.44;
      h = depth * 0.07 * (1 - u) ** 3;
    }
    // Older craters are shallower; the age varies per cell.
    total += h * (0.45 + toUnit(w.cell ^ 0xc2b2ae35) * 0.55);
  }
  return total;
}

/**
 * Transverse dune field aligned to a prevailing wind.
 *
 * The wind direction used to come from a low-frequency fbm field so crests
 * would curve like real ergs, rotating `(dx, dz)` by an angle that varied
 * continuously with position before the whole phase was multiplied by `f`.
 * That is unconditionally unstable: `f` is large specifically because it
 * encodes a *short* wavelength (a couple of kilometres, or less), and
 * multiplying a rotation by it turns the ROTATION FIELD's own spatial rate
 * into a local frequency contribution of its own — d(phase)/ds picks up a
 * term proportional to `f * d(dir)/ds`, and no realistic "slowly varying"
 * choice of `dir` makes that small once `f` is in the thousands. On Kharis
 * (duneScale 2100 m, one of the largest duneStrengths of any world) the
 * measured result was crests with a real spacing of order 10 m rather than
 * the intended ~2 km — a field of near-vertical spikes rather than dunes,
 * reproducible with `p.duneStrength` alone (toggling every other terrain term
 * off left it unchanged; toggling this one off removed it).
 *
 * A fixed wind direction, one angle per body, has none of that instability:
 * nothing here depends on position, so there is no gradient for `f` to
 * amplify. Real ergs run straight for tens of kilometres between direction
 * changes anyway, so losing the curve is a small aesthetic cost for a large
 * stability win.
 */
function duneHeight(dx: number, dy: number, dz: number, params: TerrainParams, lowlandMask: number): number {
  const p = params.profile;
  if (p.duneStrength <= 0 || lowlandMask <= 0) return 0;
  const f = frequencyFor(params.radius, p.duneScale);
  const windAngle = toUnit(hash3i(0, 0, 0, params.seed + SEED_DUNE)) * Math.PI * 2;
  const phase = (dx * Math.cos(windAngle) + dz * Math.sin(windAngle) + dy * 0.35) * f;
  // Sharp lee slope, gentle windward slope: asymmetric sawtooth-ish profile.
  const s = Math.sin(phase);
  const crest = Math.pow(clamp01(s * 0.5 + 0.5), 1.6);
  const envelope = billow3(dx * f * 0.2, dy * f * 0.2, dz * f * 0.2, { octaves: 2, seed: params.seed + SEED_DUNE + 7 });
  return p.duneStrength * crest * (0.4 + envelope * 0.6) * lowlandMask;
}

/**
 * River valleys and hillslope erosion.
 *
 * Real drainage cannot be evaluated pointwise — it needs flow accumulated from
 * upslope, which means a global simulation and a stored heightmap, and this
 * pipeline is a pure function of direction by design. What *can* be done
 * pointwise is to reproduce the geometry that drainage leaves behind, which is
 * what the eye actually reads as a river system:
 *
 *  - Channels form a branching network, not a set of independent lines. Ridged
 *    noise inverted about its crest gives exactly that: the crests of a ridged
 *    field are continuous and they join, so the troughs between them form a
 *    connected, branching valley network.
 *
 *  - Rivers run downhill and get bigger downhill. Incision is therefore scaled
 *    by how low the ground already is relative to the local relief: deep wide
 *    valleys near the coast, faint gullies near the peaks.
 *
 *  - Valleys have flat floors, not V-points. The channel term is flattened as
 *    it approaches the floor so a section reads as a floodplain between
 *    shoulders rather than as a knife cut.
 *
 * Two scales are used: trunk valleys and their tributaries, the second offset
 * so its network is not a scaled copy of the first.
 */
export function drainageHeight(
  wx: number,
  wy: number,
  wz: number,
  params: TerrainParams,
  elevation: number,
  sampleSpacing: number,
): number {
  const p = params.profile;
  // Wetter, more eroded worlds cut deeper. A world with no erosion budget gets
  // no drainage at all, which is what an airless or frozen body should show.
  const wetness = clamp01(p.erosion);
  if (wetness <= 0.01) return 0;

  const aboveSea = elevation - params.seaLevel;
  const relief = Math.max(1, p.maxElevation);
  // Height above the waterline, normalised. Near zero at the coast, one at the
  // highest ground.
  const height = clamp01(aboveSea / (relief * 0.9));

  // Discharge grows downstream: incision is strongest low down and fades out
  // near the peaks, where there is not enough catchment above to cut anything.
  const discharge = Math.pow(1 - height, 1.6) * smoothstep(0.0, 0.06, height);

  let incision = 0;

  // Trunk valleys.
  const trunkWavelength = Math.max(4000, p.continentScale * 0.05);
  const tFreq = frequencyFor(params.radius, trunkWavelength);
  const tOct = octavesFor(trunkWavelength, sampleSpacing, 4);
  const trunk = ridged3(wx * tFreq, wy * tFreq, wz * tFreq, {
    octaves: tOct,
    seed: params.seed + SEED_RIVER,
  });
  // Ridged noise peaks at 1 along its crest lines; the valley network is the
  // region just below the crest, so the channel mask is a narrow band there.
  // A wide, soft band. A narrow one with steep edges does not read as a valley,
  // it reads as a trench cut with a knife, and at coarse LOD its walls alias
  // into flat facets.
  const trunkChannel = smoothstep(0.55, 0.95, trunk);
  incision += trunkChannel * 0.72;

  // Tributaries: finer, shallower, and only where the trunk network is not
  // already carrying the water.
  const tribWavelength = Math.max(900, trunkWavelength * 0.18);
  const bFreq = frequencyFor(params.radius, tribWavelength);
  const bOct = octavesFor(tribWavelength, sampleSpacing, 3);
  if (bOct >= 1) {
    const trib = ridged3(
      wx * bFreq + 19.3,
      wy * bFreq - 7.1,
      wz * bFreq + 31.7,
      { octaves: bOct, seed: params.seed + SEED_RIVER + 101 },
    );
    incision += smoothstep(0.62, 0.96, trib) * 0.34 * (1 - trunkChannel * 0.7);
  }

  // Flat floors: once a channel is most of the way to its full depth, stop
  // cutting. This is what turns a V into a floodplain.
  const floored = incision < 0.72 ? incision : 0.72 + (incision - 0.72) * 0.25;

  const depth = relief * 0.045 * wetness * discharge * floored;
  return -depth;
}

/** Volcanic fissure and lava channel network. */
function fissureHeight(dx: number, dy: number, dz: number, params: TerrainParams): number {
  const p = params.profile;
  if (p.fissureStrength <= 0) return 0;
  const f = frequencyFor(params.radius, 90000);
  const r = ridged3(dx * f, dy * f, dz * f, { octaves: 4, seed: params.seed + SEED_FISSURE });
  // Invert the ridges into narrow incised channels.
  const channel = Math.pow(clamp01(r), 6);
  return -p.fissureStrength * p.maxElevation * 0.14 * channel;
}

/**
 * Reusable output for `domainWarp`, so the hot path does not allocate one
 * object per sample.
 */
const warpOut = { x: 0, y: 0, z: 0 };

/**
 * The continental domain warp: offsets the sample direction by a low-frequency
 * noise field before the continent mask reads it, which is what stops
 * coastlines looking like contours of a smooth blob.
 *
 * `continentField` and `elevationAt` both need it and both need it to agree
 * EXACTLY — `continentField` is what the ocean-coverage calibration solves the
 * sea-level threshold against, and `elevationAt` is what actually places the
 * ground. Two copies of the arithmetic that differed by so much as an octave
 * count would put the coastline at a different radius from the sea. They used
 * to be two copies; the shared comment on `continentField` claimed they could
 * not drift apart while ten lines directly above it were duplicated verbatim.
 *
 * Returns a shared object — read it before calling again.
 */
function domainWarp(
  dx: number,
  dy: number,
  dz: number,
  params: TerrainParams,
): { x: number; y: number; z: number } {
  const p = params.profile;
  warpOut.x = dx;
  warpOut.y = dy;
  warpOut.z = dz;
  if (p.warpStrength > 0) {
    const f = frequencyFor(params.radius, p.continentScale * 1.8);
    const s = p.warpStrength * 0.5;
    warpOut.x = dx + fbm3(dx * f, dy * f, dz * f, { octaves: 3, seed: params.seed + SEED_WARP }) * s;
    warpOut.y = dy + fbm3(dx * f, dy * f, dz * f, { octaves: 3, seed: params.seed + SEED_WARP + 41 }) * s;
    warpOut.z = dz + fbm3(dx * f, dy * f, dz * f, { octaves: 3, seed: params.seed + SEED_WARP + 83 }) * s;
  }
  return warpOut;
}

/**
 * The continental field: the scalar whose threshold separates land from sea.
 *
 * Shared by `elevationAt` and by the coverage calibration, so the two cannot
 * drift apart.
 */
export function continentField(dx: number, dy: number, dz: number, params: TerrainParams): number {
  const w = domainWarp(dx, dy, dz, params);
  return continentFieldWarped(dx, dy, dz, w.x, w.y, w.z, params);
}

/**
 * `continentField` for a caller that has already warped the sample point.
 *
 * `elevationAt` needs the warped coordinates for its own mountain and detail
 * fields, and it also needs the continent mask. Calling the public
 * `continentField` from there re-derived the identical warp — three more
 * 3-octave fbm evaluations for every vertex of every chunk, discarded
 * immediately. Same numbers either way; this just stops paying for them twice.
 */
function continentFieldWarped(
  dx: number,
  dy: number,
  dz: number,
  wx: number,
  wy: number,
  wz: number,
  params: TerrainParams,
): number {
  const contFreq = frequencyFor(params.radius, params.profile.continentScale);
  const value = fbm3(wx * contFreq, wy * contFreq, wz * contFreq, {
    octaves: 6,
    seed: params.seed + SEED_CONTINENT,
  });

  // Oceanic plates sit lower, which is what actually creates ocean basins.
  // `oceanicity` is continuous across tectonic boundaries; a binary plate flag
  // here used to turn those boundaries into exposed vertical walls.
  const plates = samplePlates(dx, dy, dz, params);
  return value + 0.2 - plates.oceanicity * 0.48;
}

/**
 * Raw elevation above the datum, in metres.
 *
 * `sampleSpacing` is the distance between neighbouring samples in the mesh being
 * built. It is used only to decide how many octaves are worth evaluating; it
 * never changes the value at a given point beyond the octaves it can resolve, so
 * two chunks at the same LOD always agree exactly on shared vertices.
 */
export function elevationAt(dx: number, dy: number, dz: number, params: TerrainParams, sampleSpacing = 0): number {
  const p = params.profile;
  const R = params.radius;

  // --- Domain warp ------------------------------------------------------
  const { x: wx, y: wy, z: wz } = domainWarp(dx, dy, dz, params);

  // --- Tectonics --------------------------------------------------------
  const plates = samplePlates(dx, dy, dz, params);
  const boundaryProximity = 1 - plates.interior;

  // --- Continental mask -------------------------------------------------
  // The threshold was calibrated so that exactly `oceanCoverage` of the surface
  // falls below it, and the base relief is arranged so that the crossover sits
  // exactly at sea level. Ocean coverage is therefore a real parameter rather
  // than a hint.
  const continent = continentFieldWarped(dx, dy, dz, wx, wy, wz, params);
  const continentNorm = smoothstep(
    params.continentThreshold - params.continentWidth,
    params.continentThreshold + params.continentWidth,
    continent,
  );

  // --- Base relief ------------------------------------------------------
  const hasOcean = p.seaLevelFraction > -1;
  // On a world with oceans the continental mask also decides where mountains
  // can form. On a dry world every point is land, so the mask must not gate
  // them away.
  const landMask = hasOcean ? continentNorm : 1;

  const elevation0 = hasOcean
    ? // continentNorm 0.5 maps exactly to the waterline.
      params.seaLevel + (continentNorm - 0.5) * 2 * p.maxElevation * 0.45
    : // No waterline to anchor to, so centre the relief on the datum instead.
      (continentNorm - 0.5) * 2 * p.maxElevation * 0.2;
  let elevation = elevation0;

  // --- Mountain belts ---------------------------------------------------
  if (p.mountainStrength > 0) {
    const mFreq = frequencyFor(R, p.mountainScale);
    const mOctaves = octavesFor(p.mountainScale, sampleSpacing, 7);
    const ridge = ridged3(wx * mFreq, wy * mFreq, wz * mFreq, {
      octaves: mOctaves,
      seed: params.seed + SEED_MOUNTAIN,
    });

    // Convergent boundaries raise ranges; divergent ones cut rifts.
    const belt = Math.pow(boundaryProximity, 2.1) * p.ridgeStrength;
    const convergent = clamp01(plates.convergence) * belt;
    const divergent = clamp01(-plates.convergence) * belt;

    elevation += ridge * p.mountainStrength * p.maxElevation * (0.35 + convergent * 1.9) * landMask;
    elevation -= divergent * p.maxElevation * 0.22;
    // Oceanic spreading ridges are the one place mountains form under water.
    elevation += plates.oceanicity * convergent * p.maxElevation * 0.18 * (1 - landMask);
  }

  // --- Eroded mid-scale relief -----------------------------------------
  const dFreq = frequencyFor(R, p.detailScale * 40);
  const dOctaves = octavesFor(p.detailScale * 40, sampleSpacing, 8);
  const eroded = erodedFbm(wx, wy, wz, dFreq, dOctaves, params.seed + SEED_EROSION, p.erosion * 6);
  elevation += eroded * p.maxElevation * 0.22 * p.roughness;

  // --- Fine detail ------------------------------------------------------
  if (p.detailStrength > 0) {
    const fFreq = frequencyFor(R, p.detailScale);
    const fOctaves = octavesFor(p.detailScale, sampleSpacing, 5);
    if (fOctaves > 0) {
      const detail = fbm3(dx * fFreq, dy * fFreq, dz * fFreq, {
        octaves: fOctaves,
        seed: params.seed + SEED_DETAIL,
      });
      elevation += detail * p.detailStrength * p.maxElevation * 0.08 * p.roughness;
    }
  }

  // --- Drainage ---------------------------------------------------------
  // Only on worlds that have, or once had, liquid on the surface.
  if (p.seaLevelFraction > -1 && elevation > params.seaLevel) {
    elevation += drainageHeight(wx, wy, wz, params, elevation, sampleSpacing);
  }

  // --- Craters, dunes and fissures --------------------------------------
  elevation += craterHeight(dx, dy, dz, params);
  elevation += fissureHeight(dx, dy, dz, params);

  const lowland = clamp01(1 - Math.abs(elevation) / (p.maxElevation * 0.35));
  elevation += duneHeight(dx, dy, dz, params, lowland * landMask);

  return elevation;
}

/** Surface radius (datum + elevation) in a direction. */
export function surfaceRadiusAt(dx: number, dy: number, dz: number, params: TerrainParams, sampleSpacing = 0): number {
  const e = elevationAt(dx, dy, dz, params, sampleSpacing);
  // On ocean worlds the *collision* surface is the sea floor, but the traversal
  // surface the player walks on is the waterline. Callers that need the sea bed
  // use `elevationAt` directly.
  return params.radius + e;
}

/** Latitude-and-altitude climate model. */
export function climateAt(dy: number, elevation: number, params: TerrainParams): { temperature: number; humidity: number } {
  const latitudeFactor = Math.abs(clamp(dy, -1, 1));
  const temperature =
    params.meanTemperature - params.polarDelta * latitudeFactor ** 1.4 - params.lapseRate * Math.max(0, elevation);
  return { temperature, humidity: 0 };
}

/** Humidity from a noise field biased by distance above sea level. */
export function humidityAt(dx: number, dy: number, dz: number, elevation: number, params: TerrainParams): number {
  if (params.seaLevel === Number.NEGATIVE_INFINITY) {
    // A dry world still has damp lowlands and dry highlands, just compressed.
    const f = frequencyFor(params.radius, params.profile.continentScale * 0.6);
    const n = fbm3(dx * f, dy * f, dz * f, { octaves: 4, seed: params.seed + SEED_HUMIDITY });
    return clamp01(0.06 + n * 0.12);
  }
  const f = frequencyFor(params.radius, params.profile.continentScale * 0.5);
  const n = fbm3(dx * f, dy * f, dz * f, { octaves: 4, seed: params.seed + SEED_HUMIDITY });
  const aboveSea = elevation - params.seaLevel;
  // Humidity falls with height above the waterline and rises near it.
  const heightFactor = clamp01(1 - aboveSea / (params.profile.maxElevation * 0.6));
  return clamp01(0.5 + n * 0.5) * (0.35 + heightFactor * 0.65);
}

/**
 * Classifies a point into one of the biomes this planet permits.
 *
 * Scoring rather than hard thresholds: each candidate biome is scored on how
 * well the local conditions fit its preferred bands, and the best match wins.
 * That makes transitions gradual (neighbouring points near a tie flip smoothly
 * as conditions drift) and makes adding a biome a data-only change.
 */
export function classifyBiome(
  params: TerrainParams,
  biomeTable: ReadonlyMap<string, BiomeBands>,
  elevation: number,
  temperature: number,
  humidity: number,
  slope: number,
  underwater: boolean,
): string {
  if (underwater) {
    if (params.biomes.includes('ocean')) return 'ocean';
  }

  const relief = params.profile.maxElevation || 1;
  const elevFraction = clamp(elevation / relief, -1, 1);

  let bestId = params.biomes[0] ?? 'rockyDesert';
  let bestScore = -Infinity;

  for (const id of params.biomes) {
    if (id === 'ocean' && !underwater) continue;
    if (id === 'cave') continue; // Caves are placed as features, not surface biomes.
    const bands = biomeTable.get(id);
    if (!bands) continue;

    let score = 0;
    score += bandScore(temperature, bands.temperature[0], bands.temperature[1]) * 1.4;
    score += bandScore(humidity, bands.humidity[0], bands.humidity[1]) * 1.1;
    score += bandScore(elevFraction, bands.elevation[0], bands.elevation[1]) * 1.0;
    score += slope > bands.maxSlope ? -1.6 * ((slope - bands.maxSlope) / bands.maxSlope) : 0.25;

    if (score > bestScore) {
      bestScore = score;
      bestId = id;
    }
  }
  return bestId;
}

export interface BiomeBands {
  temperature: [number, number];
  humidity: [number, number];
  elevation: [number, number];
  maxSlope: number;
}

/** 1 inside the band, falling off smoothly outside it. */
function bandScore(value: number, lo: number, hi: number): number {
  if (value >= lo && value <= hi) return 1;
  const width = Math.max(1e-6, hi - lo);
  const d = value < lo ? lo - value : value - hi;
  return Math.max(-1.5, 1 - (d / width) * 1.6);
}

/**
 * Full surface sample: elevation, climate, slope and biome.
 * `slope` is supplied by the caller because the mesh builder already has the
 * neighbouring heights and can compute it far more cheaply than a resample.
 */
export function surfaceSample(
  dx: number,
  dy: number,
  dz: number,
  params: TerrainParams,
  biomeTable: ReadonlyMap<string, BiomeBands>,
  slope: number,
  sampleSpacing = 0,
): SurfaceSample {
  const elevation = elevationAt(dx, dy, dz, params, sampleSpacing);
  const { temperature } = climateAt(dy, elevation, params);
  const humidity = humidityAt(dx, dy, dz, elevation, params);
  const underwater = params.seaLevel !== Number.NEGATIVE_INFINITY && elevation < params.seaLevel;
  const biomeId = classifyBiome(params, biomeTable, elevation, temperature, humidity, slope, underwater);
  return {
    elevation,
    temperature,
    humidity,
    slope,
    biomeId,
    biomeIndex: BIOME_INDEX.get(biomeId) ?? 0,
    underwater,
  };
}

/**
 * Deterministic per-location feature roll, used for resource deposits, points of
 * interest and scatter placement. Because it is a pure hash of a quantised
 * planet-space cell, the same deposit is found by every client and survives a
 * chunk being unloaded and regenerated.
 */
export function featureRoll(dx: number, dy: number, dz: number, params: TerrainParams, cellSize: number, salt: number): number {
  const scale = params.radius / Math.max(1, cellSize);
  const ix = Math.floor(dx * scale);
  const iy = Math.floor(dy * scale);
  const iz = Math.floor(dz * scale);
  return toUnit(hash3i(ix, iy, iz, params.seed + salt));
}
